import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './App.css';

import { useLoginContext } from './components/login/LoginHandler';
import { useErrorContext } from './contexts/ErrorContext';
import { useSendMessageInternal, SendMessageInternalResult } from './hooks/useSendMessageInternal';
import { useChatQueue } from './hooks/useChatQueue';
import { useLoadingContext } from './contexts/LoadingContext';

import {
  ChatMessageModel,
  ClarificationResponse,
} from './client/SmartAssistantBackendClient';

import { ChatWorkspace } from './components/app/ChatWorkspace';
import { ErrorOverlay } from './components/app/ErrorOverlay';
import { AppConfigurationEditor } from './components/app/AppConfigurationEditor';
import { unwrapMsgEnvelope } from './utils/chatMessageUtils';
import { mergeSignalRMessages } from './utils/signalRMerge';
import { SignalRProvider } from './contexts/SignalRContext';
import { createChatSyncConsumer } from './signalr/createChatSyncConsumer';
import { AdminConfigService } from './services/AdminConfigService';
import { CurrentThreadProvider, AgentSelectionModel } from './contexts/CurrentThreadContext';

type AppBodyProps = {
  adminOpen: boolean;
  setAdminOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isDarkTheme: boolean;
};

function AppBody({ adminOpen, setAdminOpen, isDarkTheme }: AppBodyProps) {
  const { setError } = useErrorContext();
  const consumer = useLoginContext();
  const { beginLoadingKey } = useLoadingContext();

  const [history, setHistory] = useState<ChatMessageModel[]>([]);
  const [chatId, setChatId] = useState<string | null>(null);
  const [awaitingResponse, setAwaitingResponse] = useState(false);
  const [submittedClarifications, setSubmittedClarifications] = useState<Record<string, boolean>>({});
  const [queuedMessages, setQueuedMessages] = useState<Array<{ id: string; text: string; createdAt: number }>>([]);
  const [agentSelection, setAgentSelectionState] = useState<AgentSelectionModel>({
    assistantInternalName: '',
    promptConfigKey: null,
    includeProfile: true,
  });

  const selectedAssistantInternalNameRef = useRef<string>('');
  const selectedDocumentAnswerPromptConfigKeyRef = useRef<string>('');
  const includeProfileRef = useRef<boolean>(true);
  const awaitingResponseRef = useRef<boolean>(false);

  const enqueueOutgoingRef = useRef<((payload: {
    text: string;
    assistantInternalName: string | null;
    promptKey: string | null;
    includeProfile: boolean;
    clarification?: ClarificationResponse;
  }) => void) | null>(null);
  const queuedMessagesRef = useRef<Array<{
    id: string;
    text: string;
    assistantInternalName: string | null;
    promptKey: string | null;
    includeProfile: boolean;
    clarification?: ClarificationResponse;
    chatId: string;
    createdAt: number;
    broadcast?: boolean;
  }>>([]);
  const nextQueuedIdRef = useRef<number>(1);

  const frontendConfigLoadedRef = useRef(false);
  const frontendConfigLoadPromiseRef = useRef<Promise<void> | null>(null);
  const frontendConfigRetryRef = useRef(0);

  const activeChatIdRef = useRef<string | null>(null);
  const chatMessageBuffersRef = useRef<Map<string, ChatMessageModel[]>>(new Map());
  const signalRApiRef = useRef<null | {
    connectSignalR: (consumer: any, chatKey: string) => Promise<void>;
    stopConnection: (chatKey: string) => Promise<void>;
    stopAllConnections: (exceptId?: string) => Promise<void>;
    clearSignalRState: (id: string) => void;
  }>(null);
  const liveShareBroadcasterRef = useRef<((messages: ChatMessageModel[], chatId?: string | null) => void) | null>(null);

  const MAX_AUTO_RETRIES = 10;

  useEffect(() => {
    awaitingResponseRef.current = awaitingResponse;
  }, [awaitingResponse]);

  useEffect(() => {
    selectedAssistantInternalNameRef.current = agentSelection.assistantInternalName;
    selectedDocumentAnswerPromptConfigKeyRef.current = agentSelection.promptConfigKey ?? '';
    includeProfileRef.current = agentSelection.includeProfile;
  }, [agentSelection]);

  const loadFrontendConfig = useCallback(async (accessToken: string, options?: { force?: boolean }) => {
    if (!accessToken) return;
    const force = options?.force ?? false;
    if (!force && frontendConfigLoadedRef.current) return;
    if (!force && frontendConfigLoadPromiseRef.current) return frontendConfigLoadPromiseRef.current;

    setError('frontendConfig', null);

    const promise = (async () => {
      const stopLoading = beginLoadingKey('frontendConfigLoad');
      try {
        await AdminConfigService.loadFrontendViewModel(accessToken);
        frontendConfigLoadedRef.current = true;
        frontendConfigRetryRef.current = 0;
      } catch (err: any) {
        console.error('LoadFrontendConfig fehlgeschlagen', err);
        if (frontendConfigRetryRef.current < MAX_AUTO_RETRIES) {
          frontendConfigRetryRef.current += 1;
          setTimeout(() => {
            loadFrontendConfig(accessToken, { force: true });
          }, 1000);
        } else {
          setError('frontendConfig', err?.message ?? 'Unbekannter Fehler');
        }
      } finally {
        frontendConfigLoadPromiseRef.current = null;
        stopLoading();
      }
    })();

    frontendConfigLoadPromiseRef.current = promise;
    return promise;
  }, [beginLoadingKey, setError]);

  const createChatId = useCallback(() => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return `chat-${Math.random().toString(36).slice(2, 11)}`;
  }, []);

  const connectSignalR = useCallback((nextConsumer: any, chatKey: string) => (
    signalRApiRef.current?.connectSignalR(nextConsumer, chatKey) ?? Promise.resolve()
  ), []);

  const stopAllConnections = useCallback((exceptId?: string) => (
    signalRApiRef.current?.stopAllConnections(exceptId) ?? Promise.resolve()
  ), []);

  useEffect(() => () => { signalRApiRef.current?.stopAllConnections(); }, []);

  const getChatBuffer = useCallback((chatKey: string, fallback: ChatMessageModel[] = []) => {
    const existing = chatMessageBuffersRef.current.get(chatKey);
    if (existing) return existing;
    const seed = [...fallback];
    chatMessageBuffersRef.current.set(chatKey, seed);
    return seed;
  }, []);

  const setChatBuffer = useCallback((chatKey: string, messages: ChatMessageModel[]) => {
    chatMessageBuffersRef.current.set(chatKey, messages);
  }, []);

  const mergeMessages = useCallback((current: ChatMessageModel[], payload: any, eventName?: string): ChatMessageModel[] => {
    return mergeSignalRMessages(current, payload, eventName, {
      shouldProcessPartial: () => awaitingResponseRef.current,
    });
  }, []);

  const signalRDeps = useMemo(() => ({
    getUserToken: consumer.getUserToken,
    unwrapMsgEnvelope,
    mergeMessages,
    getChatBuffer,
    setChatBuffer,
    activeChatIdRef,
    setHistory,
    setClarificationsToSend: () => undefined,
  }), [consumer, mergeMessages, getChatBuffer, setChatBuffer]);

  const signalRConsumer = useMemo(() => createChatSyncConsumer(signalRDeps), [signalRDeps]);

  const handleModelUpdated = useCallback((payload: {
    chatId: string | null;
    history: ChatMessageModel[];
    submittedClarifications: Record<string, boolean>;
  }) => {
    if (payload.chatId) {
      setChatId(payload.chatId);
    }
    setHistory(payload.history);
    if (payload.chatId) {
      setChatBuffer(payload.chatId, payload.history);
    }
    setSubmittedClarifications(payload.submittedClarifications);
  }, [setChatBuffer]);

  const updateLocalHistory = useCallback((nextChatId: string, normalizedOutgoing: ChatMessageModel[]) => {
    setHistory(normalizedOutgoing);
    setChatBuffer(nextChatId, normalizedOutgoing);
  }, [setChatBuffer]);

  const setAgentSelection = useCallback((next: AgentSelectionModel) => {
    setAgentSelectionState(next);
  }, []);

  const replaceHistory = useCallback((messages: ChatMessageModel[], nextChatId?: string | null) => {
    const resolvedId = typeof nextChatId === 'string' ? nextChatId : chatId;
    if (typeof nextChatId === 'string') {
      setChatId(nextChatId);
    }
    setHistory(messages);
    if (resolvedId) {
      setChatBuffer(resolvedId, messages);
    }
  }, [chatId, setChatBuffer]);

  const setCurrentThreadSelection = useCallback(async (
    nextChatId: string | null,
    nextHistory: ChatMessageModel[],
    nextAgentSelection?: AgentSelectionModel | null,
  ) => {
    await stopAllConnections(nextChatId ?? undefined);
    setChatId(nextChatId);
    setHistory(nextHistory);
    if (nextAgentSelection) {
      setAgentSelectionState(nextAgentSelection);
    }
    if (nextChatId) {
      setChatBuffer(nextChatId, nextHistory);
      await connectSignalR(consumer, nextChatId);
    }
    setAwaitingResponse(false);
    setSubmittedClarifications({});
  }, [connectSignalR, consumer, setChatBuffer, stopAllConnections]);

  const sendMessageInternal = useSendMessageInternal({
    chatId,
    history,
    createChatId,
    setChatId,
    setHistory,
    setChatBuffer,
    setAwaitingResponse,
    setSubmittedClarifications,
    setClarificationOpen: () => undefined,
    setText: () => undefined,
    loadFrontendConfig,
    updateLocalHistory,
    signalRApiRef,
  });

  const { enqueueOutgoingMessage, cancelQueuedMessage, flushQueuedMessage } = useChatQueue({
    chatId,
    createChatId,
    setChatId,
    queuedMessagesRef,
    nextQueuedIdRef,
    setQueuedMessages,
    setClarificationsToSend: () => undefined,
    setClarificationDrafts: () => undefined,
    setClarificationOpen: () => undefined,
    setClarificationKey: () => undefined,
    setText: () => undefined,
    setTextDraft: () => undefined,
    enqueueOutgoingRef,
    awaitingResponseRef,
    consumer,
    selectedAssistantInternalNameRef,
    sendMessageInternal,
    onBroadcastUserMessage: (message, nextChatId) => {
      liveShareBroadcasterRef.current?.([message], nextChatId ?? chatId);
    },
    onMessageSent: (result, broadcast) => {
      if (!broadcast) return;
      const messagesToSend: ChatMessageModel[] = [];
      if (result?.responseTurn?.length) messagesToSend.push(...result.responseTurn);
      if (messagesToSend.length > 0) {
        liveShareBroadcasterRef.current?.(messagesToSend, result?.resolvedChatId ?? chatId);
      }
    },
  });

  const addMessage = useCallback(async (
    text: string,
    clarification?: ClarificationResponse,
    options?: { broadcast?: boolean },
  ) => {
    const assistantInternalName = selectedAssistantInternalNameRef.current;
    if (!assistantInternalName) {
      alert('Bitte zuerst einen Assistenten auswählen.');
      return;
    }
    const trimmedText = text.trim();
    if (!trimmedText && !clarification?.questions?.length) {
      return;
    }

    if (awaitingResponse) {
      enqueueOutgoingMessage({
        text: trimmedText,
        assistantInternalName,
        promptKey: selectedDocumentAnswerPromptConfigKeyRef.current || null,
        includeProfile: includeProfileRef.current,
        clarification,
        broadcast: options?.broadcast ?? false,
      });
      return;
    }

    if (clarification?.messageId) {
      setSubmittedClarifications((prev) => ({ ...prev, [clarification.messageId as string]: true }));
    }

    if (options?.broadcast && liveShareBroadcasterRef.current) {
      const userMsg = new ChatMessageModel({
        role: 'user',
        content: trimmedText,
        clarification,
      });
      liveShareBroadcasterRef.current([userMsg], chatId);
    }

    const result = await sendMessageInternal(signalRConsumer, {
      text: trimmedText,
      assistantInternalName,
      promptKey: selectedDocumentAnswerPromptConfigKeyRef.current || null,
      includeProfile: includeProfileRef.current,
      clarification,
      appendLocalUserMessage: true,
      stopSpeech: true,
    }) as SendMessageInternalResult;
    if (options?.broadcast) {
      const messagesToSend: ChatMessageModel[] = [];
      if (result?.responseTurn?.length) messagesToSend.push(...result.responseTurn);
      if (messagesToSend.length > 0) {
        liveShareBroadcasterRef.current?.(messagesToSend, result?.resolvedChatId ?? chatId);
      }
    }
  }, [awaitingResponse, enqueueOutgoingMessage, sendMessageInternal, signalRConsumer, chatId]);

  const approveToolCall = useCallback((messages: ChatMessageModel[]) => {
    setHistory(messages);
  }, []);

  useEffect(() => {
    if (!awaitingResponse && queuedMessagesRef.current.length > 0) {
      flushQueuedMessage();
    }
  }, [awaitingResponse, flushQueuedMessage]);

  useEffect(() => {
    activeChatIdRef.current = chatId;
  }, [chatId]);

  const currentThreadValue = useMemo(() => ({
    threadId: chatId,
    history,
    awaitingResponse,
    queuedMessages,
    submittedClarifications,
    agentSelection,
    setAgentSelection,
    setCurrentThreadSelection,
    replaceHistory,
    approveToolCall,
    addMessage,
    cancelQueuedMessage,
    setLiveShareBroadcaster: (sender: ((messages: ChatMessageModel[], chatId?: string | null) => void) | null) => {
      liveShareBroadcasterRef.current = sender;
    },
  }), [
    addMessage,
    agentSelection,
    awaitingResponse,
    cancelQueuedMessage,
    chatId,
    history,
    approveToolCall,
    queuedMessages,
    replaceHistory,
    setAgentSelection,
    setCurrentThreadSelection,
    submittedClarifications,
  ]);

  return (
    <SignalRProvider
      incomingConsumer={signalRConsumer}
      modelState={{
        chatId,
        history,
        submittedClarifications,
      }}
      onModelUpdated={handleModelUpdated}
      onReady={(api: any) => { signalRApiRef.current = api; }}
    >
      <CurrentThreadProvider value={currentThreadValue}>
        <ChatWorkspace isDarkTheme={isDarkTheme} />
        <ErrorOverlay />
        <AppConfigurationEditor
          adminOpen={adminOpen}
          onClose={() => setAdminOpen(false)}
        />
      </CurrentThreadProvider>
    </SignalRProvider>
  );
}

export default AppBody;
